<template>
  <div class="cat-formatted-number fieldset cat-text-field">
    <cat-label
      v-if="!labelHandled"
      :labelId="id + '-input-lbl'"
      :label="label"
      :forId="id"
      :optional="!mandatory"
      :displayOptional="showOptionals"
      class="placeholder"
    ></cat-label>
    <input
      :id="id"
      ref="numberinput"
      v-model="model"
      class="cat-input"
      :placeholder="placeholder"
      :data-label-type="placeholder ? 'placeholder' : null"
      :data-label="placeholder ? placeholder : null"
      type="text"
      :aria-invalid="errors"
      :aria-required="mandatory"
      :aria-label="offscreenLabel"
      :aria-describedby="ariaDescribedby || null"
      :required="errors"
      :readonly="readonly"
      @focus="_onFocus"
      @change="_onChange"
      @input="_onInput"
      @paste="_onPaste"
      @blur="_onBlur"
      @keyup="formatInputWithEvent($event, false)"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent, inject } from "vue";
import CatLabel from "@/components/utilities/cat-label";

export default defineComponent({
  setup() {
    // Inject the global value
    const showOptionals = inject("showOptionals", true);
    const appLocale = inject("locale", "en");

    return {
      showOptionals,
      appLocale
    };
  },

  name: "CatFormattedNumber",
  components: { CatLabel },
  props: {
    placeholder: {
      type: String,
      description: "Sets the placeholder text for a field."
    },
    mandatory: {
      type: [Boolean, String],
      description: "Sets aria-required attributes for input fields and label."
    },
    id: {
      type: [String, Number],
      description: "Sets the ID attribute"
    },
    label: {
      type: String,
      description: "Sets the label to display above input fields."
    },
    offscreenLabel: {
      type: String,
      default: null,
      description: "Label to be used by accessibility."
    },
    labelHandled: {
      type: Boolean,
      default: false,
      description:
        "Flag to indicate if requiring an input field label. True means the input label is already defined somewhere else. False means a field label will rendered for this input."
    },
    value: {
      type: [String, Number],
      description: "Defines the initial default value for the associated field."
    },
    errors: [Boolean],
    readonly: {
      type: [Boolean],
      description:
        "Defines whether a user can input data into the associated field."
    },
    locale: {
      type: String,
      description: "The locale global settings use to format the number."
    },
    decimalDigits: {
      type: [Number, String],
      default: 0,
      description:
        "Number of decimal digits. 0 or undefined means a whole number."
    },
    maxLength: {
      type: [Number, String],
      default: 15,
      description:
        "Maximum number digits including the decimal digits. Decimal and thousand grouping separator are excluded. Set the value from 0 to 15."
    },
    allowNegative: {
      type: Boolean,
      default: false,
      description: "Specifies whether negative values are allowed."
    },
    ariaDescribedby: {
      type: String,
      default: "",
      description:
        "Sets the aria-describedby attribute to the input field. This should be used to point to an element that contains help text."
    }
  },
  data: function () {
    return {
      model: this.value ? this.value.toString() : "",
      lastChangeRawValue: null,
      blurChangeTimeout: null
    };
  },
  watch: {
    value: function (newvalue) {
      var _t = this;

      if (newvalue != _t.model) {
        _t.model = newvalue;
        _t.$nextTick(function () {
          _t.formatInput(false);
        });
      }
    },
    model: {
      handler(oldValue, newValue) {
        const oldVal = this.getRawValue(oldValue);
        const newVal = this.getRawValue(newValue);
        if (oldVal !== newVal) {
          console.log(oldVal, newVal);
        }
      }
    }
  },
  mounted: function () {
    var _t = this;
    _t.$nextTick(function () {
      _t.model = _t.value;
      _t.formatInput(false);
    });
  },
  beforeUnmount: function () {
    // Clean up any pending blur-triggered change emission
    if (this.blurChangeTimeout) {
      clearTimeout(this.blurChangeTimeout);
      this.blurChangeTimeout = null;
    }
  },
  computed: {
    _locale: function () {
      // If locale is passed in as prop use that else use the injected value from the app
      if (this.locale) {
        return this.locale;
      } else {
        return this.appLocale;
      }
    },
    decimalSeparator: function () {
      // Format the 0.0 to get the decimal separator
      var _t = this;
      var nf = new Intl.NumberFormat(_t._locale, {
        minimumFractionDigits: 1
      });
      var str = nf.format(0);
      var index = str.search(/\D/g);
      if (index >= 0) {
        return str[index];
      }
      return ".";
    },
    isDecimal: function () {
      return this.decimalDigits && parseInt(this.decimalDigits, 10) > 0;
    },
    maxDigits: function () {
      var max = parseInt(this.maxLength, 10);
      return isNaN(max) || max > 15 ? 15 : max;
    },
    maxDecimalDigits: function () {
      var dec = parseInt(this.decimalDigits, 10);
      return isNaN(dec) ? 2 : dec;
    }
  },
  methods: {
    _onFocus: function (event) {
      // Track the raw value when focusing to detect changes on blur
      this.lastChangeRawValue = this.getRawValue(this.model);
      // Clear any pending blur-triggered change emission
      if (this.blurChangeTimeout) {
        clearTimeout(this.blurChangeTimeout);
        this.blurChangeTimeout = null;
      }
    },
    _onChange: function (event) {
      var _t = this;
      const rawValue = this.getRawValue(_t.model);
      this.lastChangeRawValue = rawValue;

      // Clear any pending blur-triggered change emission since native change fired
      if (this.blurChangeTimeout) {
        clearTimeout(this.blurChangeTimeout);
        this.blurChangeTimeout = null;
      }

      this.$emit("ui-change", {
        event: event,
        value: rawValue,
        id: this.id ? this.id : null
      });
    },
    _onBlur: function (event) {
      this.formatInput(true);
      const currentRawValue = this.getRawValue(this.model);

      this.$emit("ui-blur", {
        event: event,
        value: currentRawValue,
        id: this.id ? this.id : null
      });

      // Safari workaround: In Safari, the native change event doesn't fire when values
      // are programmatically modified during formatting. We use a setTimeout to check
      // if the change event fires naturally. If it does (non-Safari), it will clear
      // this timeout. If it doesn't (Safari), we emit the change event ourselves.
      if (this.lastChangeRawValue !== currentRawValue) {
        this.blurChangeTimeout = setTimeout(() => {
          this.$emit("ui-change", {
            event: event,
            value: currentRawValue,
            id: this.id ? this.id : null
          });
          this.lastChangeRawValue = currentRawValue;
          this.blurChangeTimeout = null;
        }, 0);
      }
    },
    _onInput: function (event) {
      //this.model = event.target.value; //two way binding
      this.$emit("ui-input", {
        event: event,
        value: this.getRawValue(this.model),
        id: this.id ? this.id : null
      });
    },
    _onPaste: function (e) {
      if (e.clipboardData) {
        if (e.clipboardData && e.clipboardData.getData) {
          var pastedText = "";
          if (window.clipboardData && window.clipboardData.getData) {
            // IE
            pastedText = window.clipboardData.getData("Text");
          } else if (e.clipboardData && e.clipboardData.getData) {
            pastedText = e.clipboardData.getData("text/plain");
          }
        }

        // Remove any non-number at the start before parsing to int check
        pastedText = pastedText.replace(/^\D+/, "");

        // Check if its  valid input
        if (isNaN(parseInt(pastedText, 10))) {
          e.preventDefault();
          e.stopPropagation();
          return false;
        }
      }
      return true;
    },

    /**
     * Strip all non-digit and non-decimal separator.
     * This also validates that the value is within the maximum allow
     * @param value Formatted number with period as the decimal separator
     * @returns {string} Number without digits grouping and a period as the decimal separator
     */
    getRawValue: function (value) {
      if (!value) {
        return "";
      }

      var result = value;

      // See if the value passed in is already a valid number (non-localized)
      if (isNaN(Number(value))) {
        // Strip all non-digit and non-decimal separator [^0-9\.]
        const isNegative = this.allowNegative ? "\\-" : "";
        var separator = this.isDecimal ? "\\" + this.decimalSeparator : "";
        var nondigit = `[^0-9${separator}${isNegative}]`;
        result = value.replace(new RegExp(nondigit, "g"), "");
      }

      // See if there is a value to format
      if (result === "" || result === " ") {
        return "";
      }

      // Change the decimal separator to period
      if (!this.allowNegative) {
        result = result.replace(/\D/, ".");
      }

      // Need to check the maximum length
      // Number max length is 17.
      // Decimal is whole digits + 2 decimal digits
      var max = this.maxDigits;

      var decIndex = result.indexOf(".");
      var whole = decIndex > 0 ? result.substring(0, decIndex) : result;
      // Increment-allowed length if a negative sign is present allowed negative is already tested for.
      if (whole.startsWith("-")) {
        max = max + 1;
      }
      max = this.isDecimal ? max - this.maxDecimalDigits : max;
      if (whole.length > max) {
        result =
          result.slice(0, max) +
          (decIndex > 0 ? result.slice(decIndex, result.length) : "");
      }

      return result;
    },
    formatInput: function (blur) {
      this.formatInputWithEvent(null, blur);
    },
    formatInputWithEvent: function (event, blur) {
      // Working with locale formatting will return a NaN if passing in string to NumberFormat.
      // e.g. fr "12 345,00"
      // So we will be working on period as the separator internally in the component.
      var _t = this;

      // get input value
      var input_val = _t.model;

      // don't validate empty input
      if (!input_val || input_val === "") {
        return;
      }

      // don't validate when allowNegative is true and value is single '-'
      if (input_val === "-" && this.allowNegative) {
        return;
      }

      // original length
      var original_len = input_val.length;

      // initial caret position
      var caret_pos = _t.$refs.numberinput.selectionStart;

      if (this.isDecimal) {
        // Strip all non-digit and non-decimal separator [^0-9\.]
        input_val = this.getRawValue(input_val);

        // From here on, decimal separator on a raw value is a period.
        // On decimal number input, the decimal digits will only be formatted, ie. padded with 0's, on blur out
        // While still typing, it is still possible to accept whole number only or with . at the end
        if (input_val.indexOf(".") >= 0) {
          // get position of first decimal
          // this prevents multiple decimals from
          // being entered
          var decimal_pos = input_val.indexOf(".");

          // split number by decimal point
          var left_side = input_val.substring(0, decimal_pos);
          var right_side = input_val.substring(decimal_pos);
          var rawLeft = left_side;

          // add commas to left side of number
          left_side = _t.formatNumber(left_side);
          left_side = left_side ? left_side : 0;

          // Validate right side, there should only be digits
          right_side = right_side.replace(/\D/g, "");
          // Limit decimal to only the max decimal digits
          if (right_side && right_side.length > this.maxDecimalDigits) {
            right_side = right_side.substring(0, this.maxDecimalDigits);
          }

          // join number by the locale's decimal separator
          input_val = "" + left_side + _t.decimalSeparator + right_side;

          // We are leaving the input field, format the final decimal digits too
          if (blur) {
            input_val = "" + rawLeft + _t.decimalSeparator + right_side;
            input_val = this.formatDecimal(input_val);
          }
        } else {
          // no decimal entered
          // add commas to number
          // remove all non-digits
          input_val = _t.formatNumber(input_val);

          // We are leaving the input field, format the final decimal digits too
          if (blur) {
            input_val = this.formatDecimal(input_val);
          }
        }
      } else {
        input_val = this.formatNumber(input_val);
      }

      // send updated string to input
      _t.model = input_val;

      // If focusing on the amount via tab/shift+tab key we don't need to put the caret back to position
      // (skip the setSelectionRange() at the end of the code)
      if (
        event &&
        event.type === "keyup" &&
        (event.key === "Tab" ||
          event.keyCode === 9 ||
          event.key === "Shift" ||
          event.keyCode === 16)
      ) {
        return;
      }

      // put caret back in the right position - only if not blurring out of the field
      if (!blur) {
        _t.$nextTick(function () {
          var updated_len = input_val.length;
          caret_pos = updated_len - original_len + caret_pos;
          _t.$refs.numberinput.setSelectionRange(caret_pos, caret_pos);
        });
      }
    },
    /**
     *
     * @param value
     * @param decimal Decimal fraction digits
     * @returns {string}
     */
    formatNumber: function (value, decimal) {
      // Replace all non digit characters
      var result = this.getRawValue(value);
      if (result === "") {
        // No number digit to format
        return result;
      }

      var decDigits = parseInt(decimal, 10);
      if (isNaN(decDigits)) {
        decDigits = 0;
      }
      var nf = new Intl.NumberFormat(this._locale, {
        minimumFractionDigits: decDigits,
        maximumFractionDigits: decDigits
      });
      result = nf.format(result);

      return result;
    },
    formatDecimal: function (value) {
      return this.formatNumber(value, this.maxDecimalDigits);
    }
  }
});
</script>
